<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits([
  'edit',
  'delete',
  'toggle',
  'report',
  'restart',
  'replies',
]);

const { t } = useI18n();

const formatDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString();
};

const formatTime = time => {
  if (!time) return '-';
  return time;
};

const STATUS_MAP = {
  pending: { label: 'Agendada', color: 'bg-n-amber-3 text-n-amber-11' },
  running: { label: 'Enviando', color: 'bg-n-blue-3 text-n-blue-11' },
  completed: { label: 'Concluída', color: 'bg-n-teal-3 text-n-teal-11' },
  paused: { label: 'Pausada', color: 'bg-n-slate-3 text-n-slate-11' },
  failed: { label: 'Falhou', color: 'bg-n-ruby-3 text-n-ruby-11' },
};

const statusInfo = computed(() => {
  return STATUS_MAP[props.campaign.status] || STATUS_MAP.pending;
});
</script>

<template>
  <div
    class="bg-n-solid-1 border border-n-weak rounded-lg p-4 hover:shadow-md transition-shadow"
  >
    <div class="flex items-start justify-between gap-4">
      <!-- Image -->
      <div class="flex-shrink-0">
        <div
          v-if="campaign.image_url"
          class="w-16 h-16 rounded-lg overflow-hidden bg-n-alpha-2"
        >
          <img
            :src="campaign.image_url"
            :alt="campaign.title"
            class="w-full h-full object-cover"
          />
        </div>
        <div
          v-else
          class="w-16 h-16 rounded-lg bg-n-alpha-2 flex items-center justify-center"
        >
          <span class="i-lucide-image text-2xl text-n-slate-9" />
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 mb-1">
          <h3 class="text-base font-semibold text-n-slate-12 truncate">
            {{ campaign.title }}
          </h3>
          <span
            class="text-xs font-medium inline-flex items-center px-2 py-0.5 rounded-md whitespace-nowrap"
            :class="statusInfo.color"
          >
            {{ statusInfo.label }}
          </span>
        </div>

        <p class="text-sm text-n-slate-11 line-clamp-2 mb-2">
          {{ campaign.message }}
        </p>

        <div class="flex flex-wrap gap-4 text-xs text-n-slate-10">
          <div v-if="campaign.inbox" class="flex items-center gap-1">
            <span class="i-lucide-inbox" />
            <span>{{ campaign.inbox.name }}</span>
          </div>
          <div v-if="campaign.contact_list" class="flex items-center gap-1">
            <span class="i-lucide-users" />
            <span>{{ campaign.contact_list.name }} ({{
                campaign.contact_list.contacts_count
              }})</span>
          </div>
          <div class="flex items-center gap-1">
            <span class="i-lucide-calendar" />
            <span>{{ formatDate(campaign.start_date) }} -
              {{ formatDate(campaign.end_date) }}</span>
          </div>
          <div class="flex items-center gap-1">
            <span class="i-lucide-clock" />
            <span>{{ formatTime(campaign.start_time) }} -
              {{ formatTime(campaign.end_time) }}</span>
          </div>
          <div
            v-if="campaign.sent_count > 0 || campaign.failed_count > 0"
            class="flex items-center gap-2"
          >
            <span class="flex items-center gap-1 text-n-teal-11">
              <span class="i-lucide-check" />
              {{ campaign.sent_count || 0 }} enviados
            </span>
            <span
              v-if="campaign.failed_count > 0"
              class="flex items-center gap-1 text-n-ruby-11"
            >
              <span class="i-lucide-x" />
              {{ campaign.failed_count }}
            </span>
            <span
              class="flex items-center gap-1 text-n-blue-11 cursor-pointer hover:underline"
              title="Ver respostas"
              @click.stop="emit('replies')"
            >
              <span class="i-lucide-message-circle" />
              {{ campaign.reply_count || 0 }} respostas
            </span>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-2">
        <Switch
          :model-value="campaign.enabled"
          @update:model-value="emit('toggle')"
        />
        <Button
          v-if="campaign.status === 'running' || campaign.status === 'failed' || campaign.status === 'paused'"
          icon="i-lucide-rotate-ccw"
          size="sm"
          variant="ghost"
          color="blue"
          title="Reiniciar campanha"
          @click="emit('restart')"
        />
        <Button
          icon="i-lucide-file-text"
          size="sm"
          variant="ghost"
          color="slate"
          title="Relatório de envios"
          @click="emit('report')"
        />
        <Button
          icon="i-lucide-pencil"
          size="sm"
          variant="ghost"
          color="slate"
          @click="emit('edit')"
        />
        <Button
          icon="i-lucide-trash-2"
          size="sm"
          variant="ghost"
          color="ruby"
          @click="emit('delete')"
        />
      </div>
    </div>
  </div>
</template>
